/**
 * CommentComposer Component
 *
 * Input component for composing discussion comments with markdown support.
 *
 * Story FOS-4.2.4: Leaf Components Batch 2 (Collaboration)
 * Task 1: Copy Collaboration Components
 *
 * Features:
 * - Markdown support
 * - Character limit with counter (5000 chars from FOS-4.1.2)
 * - Submit via WebSocket service
 * - Typing indicator integration
 *
 * @module components/governance/CommentComposer
 * @see AC-4.2.4.1 - CommentComposer Component Migrated
 * @see AC-4.2.4.2 - Typing Indicator Works Correctly
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Send, Bold, Italic, Code, Link, List } from 'lucide-react';

/**
 * Props for CommentComposer component
 */
export interface CommentComposerProps {
  /** Callback when comment is submitted */
  onSubmit: (content: string) => Promise<void>;
  /** Callback when user starts/stops typing */
  onTypingChange?: (isTyping: boolean) => void;
  /** Callback when content changes (BL-045.4: for DraftAssistPanel integration) */
  onContentChange?: (content: string) => void;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Maximum character length (default: 5000 from FOS-4.1.2) */
  maxLength?: number;
  /** Optional class name */
  className?: string;
  /** Initial content value (used for edit mode pre-fill) */
  initialValue?: string;
}

/**
 * Character limit from FOS-4.1.2 specification
 */
const DEFAULT_MAX_LENGTH = 5000;

/**
 * Typing debounce time in milliseconds
 */
const TYPING_DEBOUNCE_MS = 1000;

/**
 * Markdown toolbar button
 */
function ToolbarButton({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
      title={label}
      aria-label={label}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

/**
 * CommentComposer - Comment input with markdown support
 *
 * @example
 * ```tsx
 * <CommentComposer
 *   onSubmit={(content) => postComment(content)}
 *   onTypingChange={(typing) => sendTypingIndicator(typing)}
 * />
 * ```
 */
export function CommentComposer({
  onSubmit,
  onTypingChange,
  onContentChange,
  disabled = false,
  placeholder = 'Share your thoughts on this proposal...',
  maxLength = DEFAULT_MAX_LENGTH,
  className = '',
  initialValue = '',
}: CommentComposerProps) {
  const [content, setContent] = useState(initialValue);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTypingRef = useRef(false);

  const charCount = content.length;
  const charRemaining = maxLength - charCount;
  const isOverLimit = charCount > maxLength;
  const canSubmit = content.trim().length > 0 && !isOverLimit && !disabled && !isSubmitting;

  // Handle typing indicator
  const updateTypingIndicator = useCallback((typing: boolean) => {
    if (isTypingRef.current !== typing) {
      isTypingRef.current = typing;
      onTypingChange?.(typing);
    }
  }, [onTypingChange]);

  // Cleanup typing indicator on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (isTypingRef.current) {
        onTypingChange?.(false);
      }
    };
  }, [onTypingChange]);

  const handleContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setContent(value);

    // BL-045.4: Notify parent of content changes for DraftAssistPanel
    onContentChange?.(value);

    // Update typing indicator
    if (value.length > 0) {
      updateTypingIndicator(true);

      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set timeout to stop typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        updateTypingIndicator(false);
      }, TYPING_DEBOUNCE_MS);
    } else {
      updateTypingIndicator(false);
    }
  }, [updateTypingIndicator, onContentChange]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    updateTypingIndicator(false);

    try {
      await onSubmit(content.trim());
      setContent('');
    } catch (err) {
      // Error handling is done by the parent
      console.error('Failed to submit comment:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [content, canSubmit, onSubmit, updateTypingIndicator]);

  // Markdown toolbar helpers
  const insertMarkdown = useCallback((prefix: string, suffix: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.slice(start, end);

    const newContent =
      content.slice(0, start) +
      prefix +
      selectedText +
      suffix +
      content.slice(end);

    setContent(newContent);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + prefix.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [content]);

  const handleBold = () => insertMarkdown('**', '**');
  const handleItalic = () => insertMarkdown('*', '*');
  const handleCode = () => insertMarkdown('`', '`');
  const handleLink = () => insertMarkdown('[', '](url)');
  const handleList = () => insertMarkdown('\n- ');

  // Handle Ctrl/Cmd + Enter to submit
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canSubmit) {
      e.preventDefault();
      handleSubmit(e);
    }
  }, [canSubmit, handleSubmit]);

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-2 ${className}`}
      data-testid="comment-composer"
    >
      {/* Markdown toolbar */}
      <div className="flex items-center gap-1 border-b border-border pb-2" data-testid="markdown-toolbar">
        <ToolbarButton icon={Bold} label="Bold (Ctrl+B)" onClick={handleBold} disabled={disabled} />
        <ToolbarButton icon={Italic} label="Italic (Ctrl+I)" onClick={handleItalic} disabled={disabled} />
        <ToolbarButton icon={Code} label="Inline code" onClick={handleCode} disabled={disabled} />
        <ToolbarButton icon={Link} label="Insert link" onClick={handleLink} disabled={disabled} />
        <ToolbarButton icon={List} label="Bullet list" onClick={handleList} disabled={disabled} />
      </div>

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={content}
        onChange={handleContentChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        aria-label="Comment text"
        className="w-full min-h-[100px] p-3 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-y disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="discussion-comment-input"
      />

      {/* Footer */}
      <div className="flex items-center justify-between">
        {/* Character counter */}
        <div className="flex items-center gap-2">
          <span
            className={`text-xs ${
              isOverLimit
                ? 'text-red-600 dark:text-red-400 font-medium'
                : charRemaining < 500
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-muted-foreground'
            }`}
            data-testid="char-counter"
          >
            {charCount} / {maxLength}
          </span>
          {isOverLimit && (
            <span className="text-xs text-red-600 dark:text-red-400">
              ({Math.abs(charRemaining)} over limit)
            </span>
          )}
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={!canSubmit}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="discussion-comment-submit"
        >
          {isSubmitting ? (
            <>
              <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              Posting...
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Post Comment
            </>
          )}
        </button>
      </div>

      {/* Keyboard shortcut hint */}
      <p className="text-xs text-muted-foreground">
        Press <kbd className="px-1 py-0.5 rounded bg-muted text-xs">Ctrl</kbd> + <kbd className="px-1 py-0.5 rounded bg-muted text-xs">Enter</kbd> to post
      </p>
    </form>
  );
}

export default CommentComposer;
